import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import url from 'url';

import {
  USER_ROLE_ANONYMOUS,
  USER_ROLE_RENTER,
  USER_ROLE_APARTMENT_LOCATOR,
  USER_ROLE_AGENT,
  USER_ROLE_SUNROOM_CLUB,
} from 'common/userRoles';

import { HOUSEHOLD_APPLICATION_STATUS_LIST } from 'common/householdConstants';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_COMPLETED,
} from 'common/tourConstants';

import {
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import isUserAgentMobileApp, {
  isUserAgentIphoneApp,
  isUserAgentAndroidApp,
} from 'common/util/isUserAgentMobileApp';

import capitalize from 'common/util/capitalize';

import { SCREEN_ID_LISTING, SCREEN_ID_LISTINGS } from 'client/constants/screenIds';
import log from 'client/lib/log';
import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

export function getIsUtilityProfitApp(screenId) {
  if (!screenId) {
    return false;
  }

  return screenId.startsWith('Utility Profit') || screenId.startsWith('Utility Setup');
}

export function getUtilityActivationForContext(state) {
  const { utilityUser, currentUser, utilityActivations } = state;
  const utilityUserId = utilityUser.id || currentUser.utilityUserId;

  if (!utilityUserId) {
    return null;
  }

  if (utilityActivations.byUtilityUserId[utilityUserId]) {
    return utilityActivations.byUtilityUserId[utilityUserId];
  }

  const utilityActivation = Object.values(utilityActivations.byShortId).find((activation) => {
    return activation.utilityUserId === utilityUserId;
  });

  return utilityActivation;
}

/**
 * Extracts application-specific contextual data from a state object.
 *
 * @param {Object} options.state               Application state.
 * @param {Object} options.navigationContext   Navigation context object.
 * return {Object}
 */
export default function makeApplicationContext({ state, navigationContext }) {
  const applicationContext = {};

  const {
    screenId,
    analyticsSessionId,
    utmValues,
    requestIp,
    location,
    userAgent,
    experiments: { activeVariants, utilityActiveVariants = {} },
  } = state.analytics;

  const {
    bookmarksByListingId,
    tours,
    householdApplications,
    household: { renterProfilesByUserId },
  } = state;

  const isUtilityProfitApp = getIsUtilityProfitApp(screenId);

  let user = state.currentUser;
  // Overwrite user with utility user if it's the utility profit app.
  if (isUtilityProfitApp && state.utilityUser.id) {
    user = state.utilityUser;
  }

  const renterProfile = renterProfilesByUserId[user.id] || null;

  applicationContext.eventId = uuidv4();
  applicationContext.analyticsSessionId = analyticsSessionId;

  applicationContext.pageName = screenId;

  // User device flags.
  applicationContext.isMobile = userAgent.isMobile;
  applicationContext.isTablet = userAgent.isTablet;
  applicationContext.isBrowser = userAgent.isBrowser;
  applicationContext.isNativeAndroid = isUserAgentAndroidApp(userAgent);
  applicationContext.isNativeIphone = isUserAgentIphoneApp(userAgent);
  applicationContext.isNativeMobile = isUserAgentMobileApp(userAgent);

  if (isUtilityProfitApp) {
    applicationContext.isUtilityProfitApp = true;
    applicationContext.userRoles = user.roles;

    if (user.id) {
      const utilityActivation = getUtilityActivationForContext(state);
      if (utilityActivation) {
        const utilityActivationContext = makeUtilityActivationContext(utilityActivation);
        for (const propName of Object.keys(utilityActivationContext)) {
          applicationContext[propName] = utilityActivationContext[propName];
        }
      }
    }
  } else {
    // Logged-in user data.
    applicationContext.userRoles = user.roles;
    if (user.roles.includes(USER_ROLE_ANONYMOUS)) {
      applicationContext.isAnonymous = true;
      applicationContext.isApartmentLocator = false;
      applicationContext.isTourGuide = false;
      applicationContext.isRenter = false;
      applicationContext.isSunroomSelect = false;
    } else {
      applicationContext.isAnonymous = false;
      applicationContext.isApartmentLocator = user.roles.includes(USER_ROLE_APARTMENT_LOCATOR);
      applicationContext.isTourGuide = user.roles.includes(USER_ROLE_AGENT);
      applicationContext.isRenter =
        !applicationContext.isApartmentLocator &&
        !applicationContext.isTourGuide &&
        user.roles.includes(USER_ROLE_RENTER);
      applicationContext.isSunroomSelect = user.roles.includes(USER_ROLE_SUNROOM_CLUB);

      applicationContext.userId = user.id;
      applicationContext.userName = user.name;
      applicationContext.userProfileImage = user.profileImage;
      applicationContext.userPhone = user.phone;
      applicationContext.userEmail = user.email;
      applicationContext.userSignupProvider = user.signupProvider;
      applicationContext.userMoveInDate = moment(user.moveInDate).toISOString();
      applicationContext.userFrontTags = user.frontTags;
      applicationContext.userLeaseSituation = user.leaseSituation;
      applicationContext.userLeaseTerm = user.leaseTerm;
      applicationContext.userLeadType = user.leadType;
      applicationContext.userSignupUtmSource = user.signupUtmSource;

      applicationContext.userSearchFilterBedrooms = user.searchFilters.bedrooms;
      applicationContext.userSearchFilterBathrooms = user.searchFilters.bathrooms;
      applicationContext.userSearchFilterPets = user.searchFilters.pets;
      applicationContext.userSearchFilterSection8 = user.searchFilters.section8;
      applicationContext.userSearchFilterMinRentPrice = user.searchFilters.minRentPrice;
      applicationContext.userSearchFilterMaxRentPrice = user.searchFilters.maxRentPrice;
      applicationContext.userSearchFilterPropertyTypes = user.searchFilters.propertyTypes;
      applicationContext.userSearchFilterUnitAmenities = user.searchFilters.unitAmenities;
      applicationContext.userSearchFilterMarketId = user.searchFilters.marketId;
      applicationContext.userBookmarksCount = Object.keys(bookmarksByListingId).length;
      applicationContext.userCompletedPrequal = renterProfile
        ? renterProfile.isPrequalCompleted
        : false;

      // Tour counts.
      // Grouping requested and scheduled tour counts.
      applicationContext.userToursRequestedCount = tours.filter((tour) => {
        return [TOUR_STATUS_REQUESTED, TOUR_STATUS_SCHEDULED].includes(tour.status);
      }).length;

      applicationContext.userToursCompletedCount = tours.filter((tour) => {
        return tour.status === TOUR_STATUS_COMPLETED;
      }).length;

      // Showing counts.
      applicationContext.userShowingsCompletedCount = tours.reduce(
        (showingsCompletedCount, tour) => {
          (tour.listings || []).forEach(({ tourListingStatus }) => {
            if (tourListingStatus === TOUR_SHOWING_STATUS_COMPLETED) {
              showingsCompletedCount += 1;
            }
          });
          return showingsCompletedCount;
        },
        0,
      );

      // Application counts.
      const applicationCountsByStatus = HOUSEHOLD_APPLICATION_STATUS_LIST.reduce((memo, status) => {
        return {
          ...memo,
          [status]: 0,
        };
      }, {});

      householdApplications.forEach((householdApplication) => {
        if (householdApplication.status in applicationCountsByStatus) {
          applicationCountsByStatus[householdApplication.status] += 1;
        }
      });

      for (const applicationStatus of Object.keys(applicationCountsByStatus)) {
        applicationContext[`userApplications${applicationStatus}Count`] =
          applicationCountsByStatus[applicationStatus];
      }
    }
  }

  // UTM values.
  applicationContext.utmSource = utmValues.source;
  applicationContext.utmMedium = utmValues.medium;
  applicationContext.utmCampaign = utmValues.campaign;
  applicationContext.utmTerm = utmValues.term;
  applicationContext.utmContent = utmValues.content;

  // Experiments.
  applicationContext.experimentActiveVariants = JSON.stringify(activeVariants);
  Object.keys(activeVariants).forEach((experimentName) => {
    const activeVariant = activeVariants[experimentName];
    applicationContext[`experiment${capitalize(experimentName)}`] = activeVariant;
  });

  applicationContext.utilityExperimentActiveVariants = JSON.stringify(utilityActiveVariants);
  Object.keys(utilityActiveVariants).forEach((experimentName) => {
    const activeVariant = utilityActiveVariants[experimentName];
    applicationContext[`utilityExperiment${capitalize(experimentName)}`] = activeVariant;
  });

  // Location values.
  applicationContext.requestIp = requestIp;
  applicationContext.locationCountry = (location || {}).country;
  applicationContext.locationRegion = (location || {}).region;
  applicationContext.locationCity = (location || {}).city;
  applicationContext.locationZip = String((location || {}).zip);
  applicationContext.locationLatitude = (location || {}).latitude;
  applicationContext.locationLongitude = (location || {}).longitude;

  // Navigation metadata.
  applicationContext.pageTitle = navigationContext.documentTitle;
  applicationContext.pageHref = navigationContext.locationHref;
  applicationContext.pageSearch = navigationContext.locationSearch;
  applicationContext.pagePath = navigationContext.locationPathname;
  applicationContext.pageHost = navigationContext.locationHostname;
  applicationContext.pageSearch = navigationContext.locationSearch;
  applicationContext.pagePort = navigationContext.locationPort;
  applicationContext.pageProtocol = navigationContext.locationProtocol;
  applicationContext.pageHash = navigationContext.locationHash;
  applicationContext.referrer = navigationContext.documentReferrer || '$direct';
  applicationContext.referringDomain = navigationContext.documentReferrer
    ? url.parse(navigationContext.documentReferrer).hostname || '$direct'
    : '$direct';

  // User viewport metadata.
  applicationContext.browserWidth = navigationContext.browserWidth;
  applicationContext.browserHeight = navigationContext.browserHeight;
  applicationContext.horizontalScroll = navigationContext.horizontalScroll;
  applicationContext.verticalScroll = navigationContext.verticalScroll;

  // User agent.
  applicationContext.userAgentBrowserName = userAgent.browserName;
  applicationContext.userAgentBrowserVersion = userAgent.browserVersion;
  applicationContext.userAgentDeviceType = userAgent.deviceType;
  applicationContext.userAgentDeviceVendor = userAgent.deviceVendor;
  applicationContext.userAgentDeviceModel = userAgent.deviceModel;
  applicationContext.userAgentEngineName = userAgent.engineName;
  applicationContext.userAgentEngineVersion = userAgent.engineVersion;
  applicationContext.userAgentOsName = userAgent.osName;
  applicationContext.userAgentOsVersion = userAgent.osVersion;
  applicationContext.userAgentCpuArchitecture = userAgent.cpuArchitecture;
  applicationContext.userAgentUserAgentString = userAgent.userAgentString;

  // Warn about not having a screenId value.
  if (!screenId) {
    const error = new Error('No screenId is present.');
    error.name = 'SrAnalyticsTrackNoScreenIdError';
    error.details = {
      applicationContext,
    };
    log.error(error);
  }

  // Add listing-specific properties when tracking events on the listing page.
  if (screenId === SCREEN_ID_LISTING && state.listing && state.listing.id) {
    const listingApplicationContext = makeListingApplicationContext(state.listing);
    for (const propName of Object.keys(listingApplicationContext)) {
      applicationContext[propName] = listingApplicationContext[propName];
    }
  }

  // Add listings-specific properties when tracking events on the listings page.
  if (screenId === SCREEN_ID_LISTINGS && state.listings) {
    const listingsApplicationContext = makeListingsApplicationContext(state.listings);
    for (const propName of Object.keys(listingsApplicationContext)) {
      applicationContext[propName] = listingsApplicationContext[propName];
    }
  }

  // Sanitize the application context object by removing empty values, except booleans and numbers.
  Object.keys(applicationContext).forEach((applicationContextFieldName) => {
    const applicationContextFieldValue = applicationContext[applicationContextFieldName];
    if (
      typeof applicationContextFieldValue !== 'boolean' &&
      (typeof applicationContextFieldValue !== 'number' || isNaN(applicationContextFieldValue)) &&
      !applicationContextFieldValue
    ) {
      delete applicationContext[applicationContextFieldName];
    }
  });

  return applicationContext;
}

/**
 * Gets the apartment fields to be added to the application context for analytics purposes.
 *
 * @param  {Object} apartment The apartment to extract the fields from.
 * @return {Object}           Object with apartment properties meant for analytics application context.
 */
export function makeApartmentApplicationContext(apartment) {
  const apartmentApplicationContext = {
    apartmentId: apartment.id,
    apartmentName: apartment.name,
    apartmentPartnerId: apartment.partnerId,
  };

  return apartmentApplicationContext;
}

/**
 * Gets the listing fields to be added to the application context for analytics purposes.
 *
 * @param  {Object} listing The listing to extract the fields from.
 * @return {Object}         Object with listing properties meant for analytics application context.
 */
export function makeListingApplicationContext(listing) {
  const listingApplicationContext = {
    listingId: listing.id,
    listingListedDate: listing.listedDate,
    listingPrice: listing.price,
    listingMaxUnitPrice: listing.maxUnitPrice,
    listingMinUnitPrice: listing.minUnitPrice,
    listingDataSource: listing.dataSource,
    listingApartmentName: listing.apartmentName,
    listingBedrooms: listing.bedrooms,
    listingBathrooms: listing.bathrooms,
    listingSquareFeet: listing.squareFeet,
    listingAvailableDate: listing.availableDate,
    listingUpdatedDate: listing.updatedDate,
    listingYearBuilt: listing.yearBuilt,
    listingMlsNumber: listing.mlsNumber,
    listingStatus: listing.status,
    listingHasPool: listing.hasPool,
    listingHasGarage: listing.hasGarage,
    listingHasMasterOnMain: listing.hasMasterOnMain,
    listingParkingSpaces: listing.parkingSpaces,
    listingPetsAllowed: listing.petsAllowed,
    listingMaximumPetsAllowed: listing.maximumPetsAllowed,
    listingPetFee: listing.petFee,
    listingPetFeeMax: listing.petFeeMax,
    listingHasWoodFlooring: listing.hasWoodFlooring,
    listingHasCarpetFlooring: listing.hasCarpetFlooring,
    listingHasTileFlooring: listing.hasTileFlooring,
    listingHasConcreteFlooring: listing.hasConcreteFlooring,
    listingHasVinylFlooring: listing.hasVinylFlooring,
    listingHasFence: listing.hasFence,
    listingHasYard: listing.hasYard,
    listingHasFencedYards: listing.hasFencedYards,
    listingHasAccessGates: listing.hasAccessGates,
    listingHasFitnessCenter: listing.hasFitnessCenter,
    listingIsHandicapFriendly: listing.isHandicapFriendly,
    listingHasHotTubSpaJacuzzi: listing.hasHotTubSpaJacuzzi,
    listingHasJoggingTrail: listing.hasJoggingTrail,
    listingHasWasherDryer: listing.hasWasherDryer,
    listingHasWasherDryerConnections: listing.hasWasherDryerConnections,
    listingHasStainlessSteelAppliances: listing.hasStainlessSteelAppliances,
    listingHasGasStove: listing.hasGasStove,
    listingHasElectricStove: listing.hasElectricStove,
    listingAddressZip: listing.addressZip,
    listingAddressCity: listing.addressCity,
    listingAddressStateCode: listing.addressStateCode,
    listingOccupantType: listing.occupantType,
    listingApplicationDescription: listing.applicationDescription,
    listingListingAgent: listing.listingAgent,
    listingMinLeaseMonths: listing.minLeaseMonths,
    listingMaxLeaseMonths: listing.maxLeaseMonths,
    listingCoverPhotoUrl: listing.sunroomPhotoUrls[0],
    listingMarketId: listing.marketId,
    listingPropertyType: listing.propertyType,
    listingTourType: listing.sunlockId
      ? 'self'
      : listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
        ? 'locator'
        : 'supra',
    listingLastActiveDate: listing.lastActiveDate,
    listingUrl: `https://sunroomrentals.com/listing/${listing.id}`,
    listingSelfTourEnabled: !!listing.sunlockId,
    listingIsSunroomDataSource: listing.dataSource === LISTING_DATA_SOURCE_SUNROOM,

    // "Facebook Catalog" requires special properties to be passed in events.
    content_ids: listing.id,
    content_type: 'home_listing',
  };

  return listingApplicationContext;
}

/**
 * Gets the listings fields to be added to the application context for analytics purposes.
 *
 * @param  {Object} listings The listings to extract the fields from.
 * @return {Object}         Object with listing properties meant for analytics application context.
 */
export function makeListingsApplicationContext(listings) {
  const listingApplicationContext = {
    resultsCount: listings.total,
  };

  if (typeof listings.viewMoreIndex === 'number' && listings.viewMoreIndex > 0) {
    listingApplicationContext.resultsCount = listings.viewMoreIndex;
  }

  return listingApplicationContext;
}

export function makeUtilityActivationContext(utilityActivation) {
  return {
    utilityPartnerId: utilityActivation.partner.id,
    utilityPartnerSlug: utilityActivation.partner.slug,
    utilityPartnerName: utilityActivation.partner.name,
    utilityPartnerStatus: utilityActivation.partner.status,
    utilityPartnerLogoUrl: utilityActivation.partner.logoUrl,
    utilityPartnerCustomerSupportPhone: utilityActivation.partner.customerSupportPhone,
    utilityActivationId: utilityActivation.id,
    utilityActivationShortId: utilityActivation.shortId,
    utilityActivationLeaseStartDate: moment(utilityActivation.leaseStartDate).unix(),
    utilityAddressId: utilityActivation.address?.id,
    utilityAddressStreetDisplay: utilityActivation.address?.streetDisplay,
    utilityAddressDisplay: utilityActivation.address?.display,
    utilityAddressStateCode: utilityActivation.address?.stateCode,
    utilityPropertyManagerEmail: utilityActivation.propertyManagerEmail,
    utilityActivationIsBulkInternet: !!utilityActivation.hasInternetAmenityEligibility,
  };
}

export function makeUtilityPartnerContext(utilityPartner) {
  return {
    utilityPartnerSlug: utilityPartner.slug,
    utilityPartnerName: utilityPartner.name,
    utilityPartnerLogoUrl: utilityPartner.logoUrl,
    utilityPartnerStatus: utilityPartner.status,
  };
}

export function makeUtilityAvailableInternetProvidersContext(utilityActivation) {
  if (!utilityActivation) {
    return {};
  }

  const utilityActivationInternetItem = utilityActivation.items.find((item) => {
    return item.utilityTypeId === UTILITY_TYPE_ID_INTERNET;
  });

  if (
    !utilityActivationInternetItem ||
    !utilityActivationInternetItem?.availableProviders?.length === 0
  ) {
    return {};
  }

  return {
    availableInternetProviders: (utilityActivationInternetItem?.availableProviders || []).map(
      (provider) => {
        return {
          title: provider.title,
          logoUrl: provider.logoUrl,
          plans: (provider.plans || []).map((plan) => {
            return {
              title: plan.title,
              price: plan.price,
              priceUnit: plan.priceUnit,
              description: plan.description,
              internetSpeedMbps: plan.internetSpeedMbps,
              termsOfServiceUrl: plan.termsOfServiceUrl,
              broadbandLabelUrl: plan.broadbandLabelUrl,
            };
          }),
        };
      },
    ),
  };
}
